import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { sb } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { LayoutDashboard, Inbox, Users, Image as ImageIcon, Sparkles, Plug, Settings, LogOut, Globe, Megaphone, CreditCard, MessageCircle, Zap } from "lucide-react";

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useT();
  const nav = useNavigate();
  const loc = useLocation();
  const [credits, setCredits] = useState(null);
  useEffect(() => {
    let on = true;
    (async () => {
      if (!user?.id) return;
      const { data } = await sb.from("profiles").select("dash_credits").eq("id", user.id).maybeSingle();
      if (on) setCredits(Number(data?.dash_credits ?? 0));
    })();
    return () => { on = false; };
  }, [user?.id, loc.pathname]);

  const items = [
    { to: "/", icon: LayoutDashboard, label: t("dashboard"), testid: "nav-dashboard" },
    { to: "/inbox", icon: Inbox, label: t("inbox"), testid: "nav-inbox" },
    { to: "/fans", icon: Users, label: t("fans"), testid: "nav-fans" },
    { to: "/vault", icon: ImageIcon, label: t("vault"), testid: "nav-vault" },
    { to: "/ai", icon: Sparkles, label: t("ai_settings"), testid: "nav-ai" },
    { to: "/mass", icon: Megaphone, label: t("mass_message"), testid: "nav-mass" },
    { to: "/connections", icon: Plug, label: t("connections"), testid: "nav-connections" },
    { to: "/pricing", icon: CreditCard, label: lang === "sk" ? "Cenník" : "Pricing", testid: "nav-pricing" },
    { to: "/settings", icon: Settings, label: t("settings"), testid: "nav-settings" },
  ];

  return (
    <aside className="w-64 shrink-0 h-screen sticky top-0 border-r border-white/5 bg-[rgb(12,13,18)] flex flex-col">
      <div className="px-6 py-7">
        <button
          onClick={() => nav("/")}
          className="text-left group"
          data-testid="sidebar-logo"
        >
          <div className="font-serif italic text-3xl tracking-tight text-white leading-none">
            <img src="/lummin-logo.png" alt="lummin" style={{width:26,height:26,objectFit:"contain",display:"inline-block",verticalAlign:"middle",marginRight:8,filter:"drop-shadow(0 0 8px rgba(177,107,255,.6))"}} />dash<span className="text-fuchsia-400">.</span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mt-2">creator panel</div>
        </button>
      </div>

      <nav className="flex-1 px-3 py-2 space-y-0.5">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-all rounded-sm ${
                  isActive
                    ? "bg-white/[0.06] text-white border-l-2 border-fuchsia-400 pl-[10px]"
                    : "text-white/55 hover:text-white hover:bg-white/[0.03] border-l-2 border-transparent"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.5} />
              <span>{it.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="px-3 py-4 border-t border-white/5 space-y-2">
        <NavLink to="/pricing" data-testid="sidebar-credits"
          className="flex items-center justify-between px-3 py-2 text-xs bg-white/[0.04] hover:bg-white/[0.08] transition-colors rounded-sm">
          <span className="flex items-center gap-2 text-white/60"><Zap size={13} strokeWidth={1.5} className="text-fuchsia-400" />{lang === "sk" ? "Kredity" : "Credits"}</span>
          <span className="text-white font-mono">{credits === null ? "…" : credits.toFixed(2)} kr</span>
        </NavLink>
        <a href="https://discord.gg/kCvCeFsZ5y" target="_blank" rel="noopener noreferrer" data-testid="sidebar-discord"
          className="flex items-center gap-2 px-3 py-2 text-xs text-white/50 hover:text-white transition-colors">
          <MessageCircle size={14} strokeWidth={1.5} />
          <span>Discord</span>
        </a>
        <button
          onClick={() => setLang(lang === "sk" ? "en" : "sk")}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/50 hover:text-white transition-colors"
          data-testid="lang-toggle"
        >
          <Globe size={14} strokeWidth={1.5} />
          <span className="uppercase tracking-wider">{lang === "sk" ? "Slovensky" : "English"}</span>
        </button>
        <div className="px-3 py-2 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs text-white truncate" data-testid="user-email">{user?.email}</div>
            <div className="text-[10px] uppercase tracking-wider text-white/40">{user?.role}</div>
          </div>
          <button
            onClick={logout}
            className="p-2 text-white/40 hover:text-rose-400 transition-colors"
            data-testid="btn-logout"
            title={t("logout")}
          >
            <LogOut size={14} strokeWidth={1.5} />
          </button>
        </div>
      </div>
      <div className="px-6 pb-4 text-[10px] tracking-[0.2em] uppercase text-white/25">from Lummin community</div>
    </aside>
  );
}
